import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { getInstance } from "../auth/index";
import { storeToRefs } from 'pinia';
import * as utils from "@/utilities";
import * as uploadProcessing from "@/utilities/uploadProcessing.js";
import {useNotebookPropsStore } from "@/store/NotebookPropsStore.js";
import {useAssetsStore } from "@/store/AssetsStore.js";
import {useEllipseStore } from "@/store/EllipseStore.js";
import { Rhino3dmLoader } from "three/examples/jsm/loaders/3DMLoader.js";
import axios from 'axios';

export const useDataUploadStore = defineStore('dataUploadStore', () => {

  // Initialize other stores
  const notebookPropsStore = useNotebookPropsStore();
  const assetsStore = useAssetsStore();
  const ellipseStore = useEllipseStore();
  const route = useRoute();
  const auth = getInstance();

  // Destructure store properties
  const {
    getPagesStructuredData
  } = storeToRefs(notebookPropsStore);

  const { getNotebook } = storeToRefs(ellipseStore);

  const validModelTypes = ["model", "map", "drawing", "data", "file"];

  /**
   * Schema for file type configuration
   * @typedef {Object} FileTypeConfig
   * @property {string} uploadType - The type of upload (e.g., 'files', 'models', 'drawings', 'maps', 'datasets', 'images').
   * @property {string} [modifyType] - The type of modification (e.g., 'model', 'drawing', 'map', 'image').
   * @property {string} [uploadUrl] - The URL to which the file should be uploaded.
   * @property {Function} handler - The handler function to process the file upload.
   */
  const fileTypeConfig = {
    'Asset': {
      uploadType: 'files',
      modifyType: 'file',
      uploadUrl: "/api/notebook/" + getNotebook.value._id + "/file/s3/put",
      handler: handleAssetUpload,
    },
    '3D': {
      uploadType: 'models',
      modifyType: 'model',
      uploadUrl: '/api/models/upload-model',
      handler: handleModelUpload,
      UploadOptions:{
        'extract_dataset':extractUserStringsFromRhinoFile,
      }
    },
    '2D': {
      uploadType: 'drawings',
      modifyType: 'drawing',
      uploadUrl: '/api/drawings/upload-drawing',
      handler: handleDrawingUpload,
    },
    'GIS': {
      uploadType: 'maps',
      modifyType: 'map',
      uploadUrl: '/api/maps/upload-map',
      handler: handleMapUpload,
    },
    'Data': {
      uploadType: 'datasets',
      modifyType: 'dataset',
      uploadUrl: '/api/datasets/upload-dataset',
      handler: handleDatasetUpload,
    },
    'DataImg': {
      uploadType: 'images',
      modifyType: 'image',
      uploadUrl: '/api/images/upload-image',
      handler: handleImageUpload,
    },
  };

  /**
   * Files draged in by the user.
   */
  const userFiles = ref([]);
  const uploadStatus = ref(false);
  const fileUpdateKey = ref(0);
  const fileUploadDone = ref(false);
  //Time stamp + upload name
  const uploadName = ref(new Date().toISOString() + ' Upload');

  // Add these new refs for tracking ingestion progress
  const totalFilesToIngest = ref(0);
  const ingestedFilesCount = ref(0);
  const isIngesting = ref(false);

  // Add these constants at the top of the file
  const FILE_BATCH_THRESHOLD = 50; // Number of files before creating a batch
  const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
  
  // Add a ref to track batches
  const fileBatches = ref({});

  // Add these new refs for tracking additional processing details
  const processingDetails = ref({
    workerActive: false,
    currentBatch: 0,
    totalBatches: 0,
    batchProgress: 0,
    processingType: '',
    lastProcessedFile: '',
    stats: {
      processed: 0,
      pending: 0,
      completed: 0,
      failed: 0
    }
  });

  const getUserFiles = computed(() => userFiles.value);
  const getUploadStatus = computed(() => uploadStatus.value);
  const getFileUpdateKey = computed(() => fileUpdateKey.value);
  const getFileUploadDone = computed(() => fileUploadDone.value);
  const getUploadName = computed(() => uploadName.value);
  // Add these computed properties for the ingestion progress
  const getTotalFilesToIngest = computed(() => totalFilesToIngest.value);
  const getIngestedFilesCount = computed(() => ingestedFilesCount.value);
  const getIsIngesting = computed(() => isIngesting.value);
  // Add this computed property
  const getFileBatches = computed(() => fileBatches.value);
  // Add this computed property for the enhanced processing details
  const getProcessingDetails = computed(() => processingDetails.value);

  /**
   * Remove file from userFiles
   */
  function removeUserFile(file) {
    const index = userFiles.value.indexOf(file);
    if (index > -1) {
      userFiles.value.splice(index, 1);
    }
    fileUpdateKey.value++;
  }

  /**
   * Clears all user files from the userFiles array.
   */
  function clearUserFiles() {
    userFiles.value.length = 0; 
    fileUpdateKey.value++;
    fileUploadDone.value = false;
  }

  function setUploadName(uploadName){
    uploadName.value = uploadName;
  }

  function triggerFileUpdateKey() {
    fileUpdateKey.value++;
  }

  /**
   * Update the upload type of a user file.
   * @param {Object} file - The file to update.
   * @param {string} type - The new upload type.
   * ["model", "map", "drawing", "data", "file"]
   */
  function updateFileUploadType(file, type) {
    // Validate type
    if (validModelTypes.includes(type)) {
      // Find the file in the userFiles array
      let userFile = userFiles.value.find(f => f === file);
      if (userFile) {
        userFile.uploadType = type;
        
        // If this is a batch, update all files in the batch
        if (userFile.isBatch && userFile.batchId) {
          const batch = fileBatches.value[userFile.batchId];
          if (batch) {
            batch.dataType = type;
          }
        }
      }
    }
    fileUpdateKey.value++;
  }

  /**
   * Update tags for a file or batch
   * @param {Object} file - The file to update
   * @param {Array} tags - The new tags
   */
  function updateFileTags(file, tags) {
    if (!file) return;
    
    file.tags = tags;
    
    // If this is a batch, update all files in the batch
    if (file.isBatch && file.batchId) {
      const batch = fileBatches.value[file.batchId];
      if (batch) {
        batch.tags = [...tags];
      }
    }
    
    fileUpdateKey.value++;
  }

  /**
   * Initial ingestion of user files - only assigns basic details
   * @param {Array} files 
   */
  async function ingestUserFiles(files) {
    const fileList = Array.from(files);
    const batchSize = 50; // Process files in batches for better UI responsiveness
    
    // Set ingestion progress tracking variables
    isIngesting.value = true;
    totalFilesToIngest.value = fileList.length;
    ingestedFilesCount.value = 0;
    
    // Group files by extension
    const filesByExtension = {};
    
    // First pass - count files by extension to identify potential batches
    fileList.forEach(file => {
      // abort if files have no extension
      if (!file.name || file.name.indexOf('.') === -1) {
        console.error('File has no extension:', file.name);
        return;
      }
      const extension = utils.getFileExtension(file.name).toLowerCase();
      if (!filesByExtension[extension]) {
        filesByExtension[extension] = [];
      }
      filesByExtension[extension].push(file);
    });
    

    // Handle file batches
    for (const [extension, extensionFiles] of Object.entries(filesByExtension)) {
      if (extensionFiles.length > FILE_BATCH_THRESHOLD && IMAGE_EXTENSIONS.includes(extension)) {
        // This is a large batch of images - create a batch entry
        createBatchFileEntry(extension, extensionFiles);
        console.log(`Created batch entry for ${extension} files`);
      } else {
        // Process normally - add individual files
        for (const file of extensionFiles) {

          // Create a bare-bones object with just the raw file
          const basicFile = {
            originalFile: file,
            processingStatus: 'pending',
            uploadStatus: 'pending',
            uploadPercentage: null,
            isValidFile: true,
            tags: [],
            isBatch: false
          };

          // Let assignFileDetails populate the final details
          const detailedFile = await assignFileDetails(basicFile);
          userFiles.value.push(detailedFile);

          ingestedFilesCount.value++;
          
          // Update UI less frequently for better performance
          if (ingestedFilesCount.value % 10 === 0 || ingestedFilesCount.value === totalFilesToIngest.value) {
            fileUpdateKey.value++;
          }
        }
      }
    }
    
    // Start background processing of files in batches
    console.log('Starting background processing of files');
    processFilesInBackground(batchSize).then(() => console.log("Processing done")) .catch(err => console.error("Processing failed", err));
  }
  
  /**
   * Creates a batch entry that represents multiple files
   * @param {string} extension - The file extension
   * @param {Array} files - The files in this batch
   */
  function createBatchFileEntry(extension, files) {
    // Determine total size
    const totalSize = files.reduce((sum, file) => sum + file.size, 0);
    const batchId = `batch_${extension}_${Date.now()}`;
    
    // Determine data type based on extension
    let dataType = 'Asset';
    let dataTypeOptions = ['Asset'];
    
    if (IMAGE_EXTENSIONS.includes(extension)) {
      dataType = 'DataImg';
      dataTypeOptions = ['DataImg', 'Asset'];
    }
    
    // Create batch record in fileBatches
    fileBatches.value[batchId] = {
      files: files,
      extension: extension,
      dataType: dataType,
      tags: [],
      processingStatus: 'pending',
      uploadStatus: 'pending',
      uploadPercentage: null
    };
    
    // Create a representation in the userFiles list
    const batchFile = {
      name: `${files.length} ${extension.toUpperCase()} files`,
      size: totalSize,
      extension: extension,
      icon: (IMAGE_EXTENSIONS.includes(extension)) ? 'mdi-image-multiple' : 'mdi-file-multiple',
      uploadStatus: 'pending',
      uploadPercentage: null,
      isValidFile: true,
      tags: [],
      dataType: dataType,
      dataTypeOptions: dataTypeOptions,
      isBatch: true,
      batchId: batchId,
      batchCount: files.length,
      completedCount: 0,
      processingStatus: 'batch_pending'
    };
    
    userFiles.value.push(batchFile);
    ingestedFilesCount.value += files.length;
    fileUpdateKey.value++;
  }
  
  /**
   * Process files in background after initial rendering
   * @param {number} batchSize - Number of files to process in each batch
   */
  async function processFilesInBackground(batchSize = 50) {
    // Initialize Web Worker if browser supports it
    let fileDetailsWorker = null;
    
    // Calculate total batches
    const totalFiles = userFiles.value.length;
    const totalBatches = Math.ceil(totalFiles / batchSize);
    
    // Set initial processing details
    processingDetails.value = {
      workerActive: false,
      currentBatch: 0,
      totalBatches: totalBatches,
      batchProgress: 0,
      processingType: 'Preparing files',
      lastProcessedFile: '',
      stats: {
        processed: 0,
        pending: totalFiles,
        completed: 0,
        failed: 0
        },
    };
    
    //if browser supports Web Workers, create a worker for parallel processing
    if (window.Worker) {
      try {
        // Create a worker blob URL for file details assignment
        const workerCode = (() => {
          const code = function() {
            self.onmessage = function(e) {
              const file = e.data.file;
              const extension = file.name.split('.').pop().toLowerCase();
              
              // Map extensions to data types (simplified version of your mapping logic)
              const extensionMapping = {
                '3dm': ['3D', 'Asset'],
                'svg': ['2D', 'Asset'],
                'geojson': ['GIS', 'Asset'],
                'json': ['Data', 'Asset'],
                'csv': ['Asset'],
                'jpeg': ['Asset', 'DataImg'],
                'jpg': ['Asset', 'DataImg'],
                'png': ['Asset', 'DataImg'],
                'bmp': ['Asset', 'DataImg'],
                'gif': ['Asset'],
              };
              
              const dataTypeOptions = extensionMapping[extension] || ['Asset'];
              
              const result = {
                index: e.data.index,
                details: {
                  dataType: dataTypeOptions[0],
                  dataTypeOptions: dataTypeOptions
                }
              };
              
              self.postMessage(result);
            };
          };
          return code.toString().replace(/^function\s*\(\)\s*{/, '').replace(/}$/, ''); 
        })();

        const blob = new Blob([workerCode], { type: 'application/javascript' });
        fileDetailsWorker = new Worker(URL.createObjectURL(blob));
        
        processingDetails.value.workerActive = true;
        processingDetails.value.processingType = 'Using Web Worker for parallel processing';
        
        fileDetailsWorker.onmessage = (e) => {
          const { index, details } = e.data;
          // Update file with processed details - make sure to trigger reactivity
          if (userFiles.value[index]) {
            // Use direct property assignment instead of Object.assign for better reactivity
            userFiles.value[index].dataType = details.dataType;
            userFiles.value[index].dataTypeOptions = details.dataTypeOptions;
            userFiles.value[index].processingStatus = 'details_assigned';
            
            // Update processing stats
            processingDetails.value.stats.processed++;
            processingDetails.value.stats.pending--;
            processingDetails.value.stats.completed++;
            processingDetails.value.lastProcessedFile = userFiles.value[index].name;
            
            // Force trigger reactivity
            userFiles.value = [...userFiles.value];
            
            // Update UI more frequently
            fileUpdateKey.value++;
          }
        };
        
      } catch (error) {
        console.error('Error creating Web Worker:', error);
        fileDetailsWorker = null;
        processingDetails.value.workerActive = false;
        processingDetails.value.processingType = 'Using sequential processing (worker failed)';
      }
    } else {
      processingDetails.value.processingType = 'Using sequential processing (workers not supported)';
    }
    
    // Process files in batches
    for (let batch = 0; batch < totalBatches; batch++) {
      const start = batch * batchSize;
      const end = Math.min(start + batchSize, totalFiles);
      
      // Update batch progress
      processingDetails.value.currentBatch = batch + 1;
      processingDetails.value.batchProgress = 0;
      
      // Process this batch
      await processFileBatch(start, end, fileDetailsWorker);
      
      // Update batch completion
      processingDetails.value.batchProgress = 100;
      
      // Allow UI to update between batches
      await new Promise(resolve => setTimeout(resolve, 0));
    }
    
    // Clean up worker when done
    if (fileDetailsWorker) {
      fileDetailsWorker.terminate();
      processingDetails.value.workerActive = false;
    }
    
    // Update processing type for CSV conversion
    processingDetails.value.processingType = 'Processing CSV files';
    
    // After all files have basic details, process CSV files to convert to JSON
    await processCSVFiles();
    
    // Update processing type for dataset processing
    processingDetails.value.processingType = 'Processing datasets';
    
    // Then process dataset files
    await processAllDatasetFiles();
    
    // Finalize ingestion process
    processingDetails.value.processingType = 'Complete';
    isIngesting.value = false;
    fileUpdateKey.value++;

    console.log('File ingestion complete');
  }
  
  /**
   * Process a batch of files, either with Web Worker or directly
   * @param {number} start - Start index in userFiles array
   * @param {number} end - End index in userFiles array
   * @param {Worker} worker - Web Worker instance if available
   */
  async function processFileBatch(start, end, worker) {
    const promises = [];
    const batchSize = end - start;
    let processed = 0;
    
    for (let i = start; i < end; i++) {
      if (i >= userFiles.value.length) break;
      
      const file = userFiles.value[i];
      
      if (file.processingStatus !== 'pending') {
        processed++;
        processingDetails.value.batchProgress = Math.round((processed / batchSize) * 100);
        continue;
      }
      
      if (file.isBatch) {
        processed++;
        processingDetails.value.batchProgress = Math.round((processed / batchSize) * 100);
        continue; // Skip batch entries here - they're processed differently
      }
      
      if (worker) {
        // Use Web Worker for parallel processing
        worker.postMessage({
          file: {
            name: file.name,
            size: file.size,
            type: file.type
          },
          index: i
        });
        
        // No promises to await when using workers
        processingDetails.value.lastProcessedFile = file.name;
      } else {
        // Fallback to synchronous processing
        promises.push((async () => {
          const originalFile = file.originalFile;
          processingDetails.value.lastProcessedFile = file.name;
          
          try {
            const detailedFile = await assignFileDetails(originalFile);
            
            // Update file with detailed information
            Object.assign(userFiles.value[i], detailedFile);
            delete userFiles.value[i].originalFile; // Clean up reference to avoid memory leaks
            userFiles.value[i].processingStatus = 'details_assigned';
            
            // Update processing stats
            processingDetails.value.stats.processed++;
            processingDetails.value.stats.pending--;
            processingDetails.value.stats.completed++;
          } catch (error) {
            console.error(`Error processing file ${file.name}:`, error);
            processingDetails.value.stats.failed++;
            processingDetails.value.stats.pending--;
          }
          
          processed++;
          processingDetails.value.batchProgress = Math.round((processed / batchSize) * 100);
        })());
      }
    }
    
    // Process any batch entries
    const batchEntries = userFiles.value.filter(f => f.isBatch && f.processingStatus === 'batch_pending');
    for (const batchEntry of batchEntries) {
      processingDetails.value.lastProcessedFile = `Batch: ${batchEntry.name}`;
      await processBatchEntry(batchEntry);
    }
    
    // Wait for all promises to resolve if not using workers
    if (promises.length > 0) {
      await Promise.all(promises);
    }
    
    fileUpdateKey.value++;
  }

  /**
   * Process CSV files and convert them to JSON
   */
  async function processCSVFiles() {
    // Update processing details
    processingDetails.value.processingType = 'Converting CSV files to JSON';

    // Find all CSV files
    const csvFiles = userFiles.value.filter(file => 
      file.extension === 'csv' && 
      file.originalFile && 
      (file.processingStatus === 'details_assigned' || file.processingStatus === 'pending')
    );

    console.log(`Found ${csvFiles.length} CSV files to process`);

    if (csvFiles.length === 0) return;

    for (let i = 0; i < csvFiles.length; i++) {
      const csvFile = csvFiles[i];
      processingDetails.value.lastProcessedFile = csvFile.name;

      try {
        console.log(`Converting CSV to JSON: ${csvFile.name}`);

        // Update conversion progress
        csvFile.processingStatus = 'converting_to_json';
        fileUpdateKey.value++;

        // Convert CSV to a new file object
        const jsonBlob = await converCSVtoJSON(csvFile.originalFile);

        // Create a JSON file-like object carrying over CSV file props
        const jsonFileObj = {
          originalFile: jsonBlob,
          // Keep or copy any properties from your csvFile
          tags: csvFile.tags ? [...csvFile.tags] : [],
          processingStatus: 'pending',
        };

        // Populate final details
        const detailedJsonFile = await assignFileDetails(jsonFileObj);
        console.log(`Details assigned to JSON file:`, detailedJsonFile);

        // Override JSON file data type to be 'Data' by default
        detailedJsonFile.dataType = 'Data';

        // Copy other CSV-based properties if needed (e.g., uploadPercentage)
        // ...

        // Add to userFiles array
        userFiles.value.push(detailedJsonFile);
        console.log('Added JSON file to userFiles:', detailedJsonFile.name);

        // Mark CSV file as processed
        csvFile.processingStatus = 'converted_to_json';
        csvFile.jsonFileName = detailedJsonFile.name; // Reference to converted JSON file

        // Update UI more frequently
        if (i % 3 === 0 || i === csvFiles.length - 1) {
          fileUpdateKey.value++;
        }
      } catch (error) {
        console.error(`Error converting CSV file ${csvFile.name}:`, error);
        csvFile.processingStatus = 'error';
        csvFile.error = error.message;
        processingDetails.value.stats.failed++;
      }
    }

    fileUpdateKey.value++;
  }
  
  /**
   * Converts CSV to JSON
   * @param {File} csv
   * @returns {Object} file
   */
  async function converCSVtoJSON(csv) {
    try {
      
      const file = await utils.csvToJson(csv);
      
      // Validate that the conversion produced a valid file
      if (!file || !file.name || !file.size) {
        throw new Error('CSV conversion failed to produce a valid file');
      }
      
      console.log(`CSV converted to JSON: ${file.name}, size: ${file.size}`);
      return file;
    } catch (error) {
      console.error('Error in CSV to JSON conversion:', error);
      throw error; // Rethrow to be caught by the caller
    }
  }

  /**
   * Process all dataset files
   */
  async function processAllDatasetFiles() {
    const datasetFiles = userFiles.value.filter(f => 
      f.extension === 'json' && 
      f.processingStatus !== 'dataset_processed'
    );

    console.log(`Found ${datasetFiles.length} dataset files to process`);
    console.log('Dataset files:', datasetFiles);
    
    if (datasetFiles.length > 0) {
      await processDatasetFiles(datasetFiles);
      
      // Mark all processed dataset files
      datasetFiles.forEach(file => {
        file.processingStatus = 'dataset_processed';
      });
      
      fileUpdateKey.value++;
    }
  }

  function chunkArray(array, size) {
    const results = [];
    for (let i = 0; i < array.length; i += size) {
      results.push(array.slice(i, i + size));
    }
    return results;
  }

  async function startUpload() {
    uploadStatus.value = true;
    
    // First process regular files (not batch files or datasets)
    const regularFiles = userFiles.value.filter(f => !f.isBatch && f.dataType !== 'Data');
    await uploadFiles(regularFiles);
    fileUpdateKey.value++;

    // Then handle batch files
    const batchEntries = userFiles.value.filter(f => f.isBatch);
    for (const batchEntry of batchEntries) {
      if (batchEntry.batchId && fileBatches.value[batchEntry.batchId]) {
        const batch = fileBatches.value[batchEntry.batchId];
        const batchFiles = batch.files;
        
        if (!batchFiles || batchFiles.length === 0) continue;
        
        // Set initial progress
        batchEntry.processingStatus = 'batch_uploading';
        batchEntry.uploadPercentage = 0;
        batchEntry.completedCount = 0;
        batchEntry.processingStatus = 'batch_uploading';
        batchEntry.totalCount = batchFiles.length;
        fileUpdateKey.value++;

        if (batch.dataType === 'DataImg') {
          // Split your files into chunks of 25
          const chunkedFiles = chunkArray(batchFiles, 25);

          for (const chunk of chunkedFiles) {
            try {
              await handleBatchImageUpload(batchEntry, chunk, fileTypeConfig['DataImg']);
              fileUpdateKey.value++;
              //batchEntry.uploadPercentage = 100;
            } catch (err) {
              console.error("Batch image upload failed:", err);
            }
          }
        } else {
          // Process each file in the batch
          for (let i = 0; i < batchFiles.length; i++) {
            const actualFile = batchFiles[i];
            
            // Create a file-like object with necessary properties
            const tempFile = {
              name: actualFile.name,
              size: actualFile.size,
              lastModified: actualFile.lastModified,
              type: actualFile.type,
              originalFile: actualFile,
              // Add properties from the batch entry
              extension: batchEntry.extension,
              dataType: batchEntry.dataType,
              tags: [...batchEntry.tags], // Clone the tags array
              // Add functions needed for upload
              arrayBuffer: () => actualFile.arrayBuffer(),
              slice: (...args) => actualFile.slice(...args),
              // Track upload progress
              uploadingStatus: 'pending',
              uploadPercentage: 0
            };
            
            // Upload the individual file
            const config = fileTypeConfig[batchEntry.dataType];
            if (config) {
              try {
                await config.handler(tempFile, config);
                
                // Update the batch progress
                batchEntry.completedCount++;
                batchEntry.uploadPercentage = Math.round((batchEntry.completedCount / batchEntry.totalCount) * 100);
                fileUpdateKey.value++;
              } catch (error) {
                console.error('ERROR: Failed to upload batch file', actualFile.name, error);
              }
            }
          }
          
          // Ensure progress is at 100% when complete
          batchEntry.uploadPercentage = 100;
          fileUpdateKey.value++;
        }
      }
    }

    // Check for 3D files with dataset extraction
    const models = userFiles.value.filter(
      (f) => !f.isBatch && f.dataType === '3D' &&
            f.uploadOptions &&
            f.uploadOptions.extract_dataset === true
    );

    for (const model of models) {
      try {
        const json = await extractUserStringsFromRhinoFile(model);
      } catch (error) {
        console.error(`Error extracting user strings from ${model.name}:`, error);
      }
    }

    // Upload datasets (skip batch entries)
    const datasets = userFiles.value.filter(f => !f.isBatch && f.dataType === 'Data');
    await uploadFiles(datasets);
    fileUpdateKey.value++;
    uploadStatus.value = false;

    let assets = await assetsStore.setAllAssetsInNotebook();
    if (assetsStore.getSelectedPageDataset == null && assetsStore.getSelectedPageDatasets.length > 0) {
      let set = await assetsStore.setSelectedPageDataset(assetsStore.getSelectedPageDatasets[0]);
    }
    fileUploadDone.value = true;
  }

  // Unified upload function
  async function uploadFiles(files) {
    if (files.length === 0) return;
    return Promise.all(
      files.map(async (file) => {

        //get sepecific model config based on file dataType
        const config = fileTypeConfig[file.dataType];
        if (!config) {
          console.error('ERROR: File type not recognized', file.name, file.dataType);
          return false;
        }
        try {
          await config.handler(file, config);
        } catch (error) {
          console.error('ERROR: Failed to upload file', file.name, error);
        }
      })
    );
  }

  /**
   *  Handler for asset or generic files uploads
   * @param {*} file - the file to upload
   * @param {*} config 
   */
  async function handleAssetUpload(file, config) {
    return new Promise(async (resolve, reject) => {

      const allPageIds = getPagesStructuredData.value.map((page) => page._id);

      // Create temporary file record
      const data = {
        notebook: getNotebook.value._id,
        folder: getNotebook.value.rootFolder,
        lastModified: file.lastModified,
        name: file.name,
        nickname: file.name,
        author: auth.user.name,
        size: file.size,
        tags:[uploadName.value, ...file.tags],
        groups: file.groups || [],
        type: file.type || file.extension,
        pages: [...allPageIds],
      };

      //validate file type
      file.isValidFile = true;

      // #1. Create temporary record
      const tempPostRoute = `/api/notebook/${getNotebook.value._id}/file/create-temp/?urlType=${route.name}`;
      const tempFileResponse = await auth.$api.post(tempPostRoute, data);
      if (tempFileResponse.status !== 201) {
        console.error('Failed to create temporary file record');
        return;
      }
      const newFile = tempFileResponse.data.data;

      // #2. Upload to S3
      const callback = async (progresses, uploadedKey) => {
        const progress = progresses.find(p => p.name === file.name);
        file["uploadPercentage"] = progress.percent*0.9;
        fileUpdateKey.value++;
        if (uploadedKey) {
          newFile.s3Key = uploadedKey;
          newFile.s3_pointers = [uploadedKey];
          newFile.Key = uploadedKey;
          newFile.allowAnonymousAccess = true;
          const savePostRoute = `/api/notebook/${getNotebook.value._id}/file/save/?urlType=${route.name}`;
          let response = await auth.$api.post(savePostRoute, newFile);
          if (response.status == 200) {
            file["uploadPercentage"] = 100;
            resolve(true)
          }
          else reject('could not upload file', response.status, response)
        }
      }

      const path = `notebooks/${getNotebook.value._id}/files/`;
      const s3Key = path + newFile._id + "?" + file.name;

      await uploadS3(
        config.uploadUrl,
        [file],
        path,
        newFile._id,
        getNotebook.value._id,
        uploadProcessing.dataPayloadGeneratorForFiles,
        callback
      );
      fileUpdateKey.value++;
    });
  }

  //handleer for Dataset Upload.
  async function handleDatasetUpload(file, config) {
    console.log('dataset upload', file);
    return new Promise(async (resolve, reject) => {
      // Validate the file content
      let isValid = await uploadProcessing.isValidJsonFile(file.originalFile);
      if (!isValid) {
        console.error('ERROR: File type not recognized');
        return;
      }
      file.isValidFile = true;

      const datasetId = 'eDataset_';

      // #2. Upload the dataset file to S3
      const path = `notebooks/${getNotebook.value._id}/${config.uploadType}/`;

      const dataPayloadGenerator = uploadProcessing.dataPayloadGeneratorForAssets;

      const callback = async (progresses, uploadedKey) => {
        const getFileName = async (file) => {
          if (file instanceof Promise) {
            file = await file;
          }
          return file.name;
        };

        const progress = progresses.find(async (f) => {
          const fileName = await getFileName(file);
          return f.name === fileName;
        });

        file["uploadPercentage"] = progress.percent*0.9;
        fileUpdateKey.value++;
        if (uploadedKey) {
          // #3. Save or update the dataset record with S3 key

          const allPageIds = getPagesStructuredData.value.map((page) => page._id);

          if (file instanceof Promise) {
            file = await file;
          }

          const postData = {
            name: file.name,
            s3_pointers: uploadedKey,
            pages: [...allPageIds],
            notebook: getNotebook.value._id,
            tags:[uploadName.value, ...file.tags],
            author: auth.user.name,
            size: file.size,
          };

          const endpoint = `/api/${config.uploadType}/new`;
          //const endpoint = `/api/${config.modifyType}/${assetId}/edit-info`;
          let response = await auth.$api.post(endpoint, postData);
          if (response.status == 201) {
            file["uploadPercentage"] = 100;
            resolve(true)
          }
          else reject('could not upload file', response.status, response)

        }
      };

      await uploadS3(
        config.uploadUrl,
        [file],
        path,
        datasetId,
        getNotebook.value._id,
        dataPayloadGenerator,
        callback
      );
      fileUpdateKey.value++;

    });
  }

  // Handler for model uploads.
  async function handleModelUpload(file, config) {
    await handleGenericAssetUpload(file, config);
  }

  // Handler for drawing uploads.
  async function handleDrawingUpload(file, config) {
    await handleGenericAssetUpload(file, config);
  }

  // Handler for map uploads.
  async function handleMapUpload(file, config) {
    await handleGenericAssetUpload(file, config);
  }

  // Handler for Image uploads.
  async function handleImageUpload(file, config) {

    const nickname = file.name.replace(/\.[^/.]+$/, "");
    let imageSpecificProps = {
      nickname: nickname,
      extention: file.extension,
      size: file.size,
      tags: [uploadName.value, ...file.tags],
    };
    await handleGenericAssetUpload(file, config, imageSpecificProps);
  }

  /**
   * handler for models, drawings, maps, and datasets
   * @param {*} file - the file to upload
   * @param {*} config - the configuration for the file type
   * @param {*} assetType - the type of asset to upload
   * @param {*} schemaOverload - additional properties to add to the schema
   */
  // Generic handler for models, drawings, maps
  async function handleGenericAssetUpload(file, config, schemaOverload = null) {
    return new Promise(async (resolve, reject) => {
      
      // Validate the file content
      file.isValidFile = true;

      // #1. Create temporary record
      const allPageIds = getPagesStructuredData.value.map((page) => page._id);
      const tempPostData = {
        name: file.name,
        pages: [...allPageIds],
        notebook: getNotebook.value._id,
        tags:[uploadName.value, ...file.tags],
        author: auth.user.name,
        size: file.size,
      };
      const tempPostRoute = `/api/${config.uploadType}/new`;
      const tempAssetResponse = await auth.$api.post(tempPostRoute, tempPostData);
      if (tempAssetResponse.status !== 201) throw new Error("Error creating temp model record");

      //Mongo Record ID
      const assetId = tempAssetResponse.data.data._id;

      // #2. Upload the file to S3
      // S3 symbolic folder path
      const path = `notebooks/${getNotebook.value._id}/${config.uploadType}/`;

      // #3. Callback function to modify mongo record after S3 upload
      const callback = async (progresses, uploadedKey) => {
        const progress = progresses.find(f => f.name === file.name);
        file["uploadPercentage"] = progress.percent*0.9;
        fileUpdateKey.value++;
        if (uploadedKey) {
          let postData = {
            name: file.name,
            s3_pointers: uploadedKey,
            pages: [...allPageIds],
            notebook: getNotebook.value._id,
          };
          if (schemaOverload) {
            postData = { ...postData, ...schemaOverload };
          }
          
          const endpoint = `/api/${config.modifyType}/${assetId}/edit-info`;

          let response = await auth.$api.post(endpoint, postData);
          if (response.status == 200) {
            file["uploadPercentage"] = 100;
            resolve(true)
          }
          else reject('could not upload file', response.status, response)
        }
      }
      //await utils.uploadModelsToS3(
      await uploadS3(
          config.uploadUrl,
          [file],
          path,
          assetId, 
          getNotebook.value._id,
          uploadProcessing.dataPayloadGeneratorForAssets,
          callback
      );
      fileUpdateKey.value++;
    });
  }

  async function handleBatchImageUpload(batchEntry, batchFiles, config) { 
    const authService = getInstance();

    const files = batchFiles.map((file) => {
      return {
          name: file.name,
          size: file.size,
          type: file.type,
          originalFile: file,
          extension: batchEntry.extension,
          dataType: batchEntry.dataType,
          tags: [...batchEntry.tags],
          uploadPercentage: 0,
          uploadStatus: "pending",
      }
    });


    //generate s3 path keys for each file
    let time = new Date().toISOString();
    files.forEach((file) => {
      let s3PathKey = `notebooks/${getNotebook.value._id}/${config.uploadType}/${time}-${file.name}`;
      file.s3_pointers = [s3PathKey];
      file["uploadStatus"] = "pending";
      file["uploadPercentage"] = 0;
    });

    //reduce the files to only the s3_pointers and type
    //this is the format the backend needs to get signed urls for batch upload
    const requestFiles = files.map((file) => {
      return{
        s3_pointers: file.s3_pointers[0],
        type: file.type,
      }
    })
    
    // Make the API call to get the signed URLs for all images
    const endpoint = `/api/files/signed-url-batch`;
    const dataPayload = {'files': requestFiles}
    let response = await authService.$api.post(endpoint, dataPayload);

    if (response.status !== 201) {
      console.error('Failed to get signed URL for batch upload');
      return;
    }

    let signedUrls = response.data.data;

    // Upload the files to S3
    const uploadPromises = files.map((file, index) => {
      const signedUrl = signedUrls[index].signedUrl;
      return uploadFileWithAxios(file, signedUrl);
    });

    // Wait for all uploads to complete
    try {
      await Promise.all(uploadPromises);
      //console.log('All files uploaded successfully!');
    } catch (error) {
      console.error('Error uploading files:', error);
    }

    //create the mongo record for each file
    const allPageIds = getPagesStructuredData.value.map((page) => page._id);
    const notebookId = getNotebook.value._id;
    const recordPostRoute = `/api/${config.uploadType}/new`;

    const mondoRecords = files.map((file) => {
      return {
        name: file.name,
        nickname: file.name.replace(/\.[^/.]+$/, ""), // Remove extension for nickname
        extension: file.extension,
        pages: [...allPageIds],
        notebook: notebookId,
        tags: [uploadName.value, ...file.tags],
        author: auth.user.name,
        size: file.size,
        s3_pointers: file.s3_pointers,
      };
    });

    // Create records
    const createRecordsPromises = mondoRecords.map((record) => {
      return authService.$api.post(
        recordPostRoute, 
        record
      ).then((response) => {
        batchEntry.completedCount++;
        batchEntry.uploadPercentage = Math.round((batchEntry.completedCount / batchEntry.totalCount) * 100);
        fileUpdateKey.value++;
      }).catch((error) => {
        console.error('Error creating record:', error);
        batchEntry.uploadStatus = "error";
      });
    });

    try {
      await Promise.all(createRecordsPromises);
      //console.log('All records created successfully!');
    } catch (error) {
      console.error('Error creating records:', error);
    } 

    // Update the upload status and percentage for each file
    files.forEach((file) => {
      file.uploadStatus = "complete";
      file.uploadPercentage = 100;
    });
    fileUpdateKey.value++;
  }

  /**
   * checks file extension and assigns upload props
   * like dataType, extension, uploadPercentage, and icon.
   * @param {File} file
  */
  async function assignFileDetails(fileObj) {
    // Pull out the raw File or Blob
    const rawFile = fileObj.originalFile;

    // Derive extension, icon, and data-type options.
    const extension = utils.getFileExtension(rawFile.name).toLowerCase();
    const dataTypeOptions = getDataTypeOptions(rawFile);
    let dataType = dataTypeOptions[0];

    const detailedProps = {
      name: rawFile.name,
      size: rawFile.size,
      lastModified: rawFile.lastModified,
      type: rawFile.type,
      extension: extension,
      icon: utils.fileIcon(rawFile.name),
      dataType: dataType,
      dataTypeOptions: dataTypeOptions,
      uploadPercentage: null,
      isValidFile: true,
      processingStatus: 'details_assigned',
      tags: fileObj.tags ?? []
    };

    
    // Add upload options if available for this data type
    if (fileTypeConfig[dataType] && fileTypeConfig[dataType].UploadOptions) {
      detailedProps.uploadOptions = fileTypeConfig[dataType].UploadOptions;
    }

    // Merge the new metadata with the original structure,
    // preserving rawFile in `originalFile` only.
    return {
      ...fileObj,
      ...detailedProps
    };
  }

  /**
   * Iterates over JSON files 
   * - Reads and parses each JSON file.
   * - Identifies valid file and image attachment headers.
   * - Processes attachemnt headers and groups using processHeadersAndGroups.
   * - Processes each record to find attachments and their groups using processRecordAttachments.
   * - Updates otherFiles to mark files as attachments and records their groups.
   * @param {*} datasetFiles 
   */
  async function processDatasetFiles(datasetFiles) {
      //iterate through json files
      for (const jsonFile of datasetFiles) {
        //Read the JSON file - use originalFile if available
        let fileToRead = jsonFile.originalFile || jsonFile;
        let jsonText;

        console.log('Processing JSON file:', jsonFile.name);
        
        try {
          jsonText = await utils.readJsonFile(fileToRead);
          console.log('json text', jsonText);

          console.log('is it valid json', uploadProcessing.isJsonString(jsonText));
          //Only Process valid JSON files
          if (uploadProcessing.isJsonString(jsonText)) {
            //Parse the JSON file
            let jsonData = JSON.parse(jsonText);

            //check that each json file returns a itterable array, if not, change the dataType to Asset
            if (!Array.isArray(jsonData)) {
              jsonFile.dataType = "Asset";
              return; 
            }

            //Array of headers that include attachments
            //& group names to used when attachment headers get flattened into single header
            //& extracted file names and thier assosiated groups
            let validFileHeaders = uploadProcessing.findValidHeaderKeys(jsonData, uploadProcessing.validFileHeaders);
            let fileGroups = {};
            let fileAttachmentGroups = {};

            let validImageHeaders = uploadProcessing.findValidHeaderKeys(jsonData, uploadProcessing.validImageHeaders);
            let imageGroups = {};
            let imageAttachmentGroups = {};

            //Produce Groups for each attachements header
            if (validFileHeaders.length === 0 && validImageHeaders.length === 0){
              //no valid headers found
              //console.log('No valid attachment headers found in JSON file', jsonFile.name);
            } else {
              // Process valid file & image headers
              fileGroups = processHeadersAndGroups(jsonData, uploadProcessing.validFileHeaders);
              imageGroups = processHeadersAndGroups(jsonData, uploadProcessing.validImageHeaders);
            }

            // Process each record in the JSON file to find attachments names and groups
            for (const record of jsonData) {
              // Process each record in the JSON file
              fileAttachmentGroups = uploadProcessing.mergeArrays(fileAttachmentGroups, processRecordAttachments(record, fileGroups));
              imageAttachmentGroups = uploadProcessing.mergeArrays(imageAttachmentGroups, processRecordAttachments(record, imageGroups));
            }
            
            // Check uploaded files to label data attachments and record their groups
            for (let userFile of userFiles.value) {
              if(fileAttachmentGroups.hasOwnProperty(userFile.name)){
                uploadProcessing.updateGroups(userFile, fileAttachmentGroups[userFile.name]);
              } 

              if (imageAttachmentGroups.hasOwnProperty(userFile.name)) {
                userFile.dataType = "DataImg";
                uploadProcessing.updateGroups(userFile, imageAttachmentGroups[userFile.name]);
              } 
            }
          }
        } catch (error) {
          console.error(`Error processing JSON file ${jsonFile.name}:`, error);
          jsonFile.processingStatus = 'error';
          jsonFile.error = error.message;
        }
      }
  }
  
  /**
   * checks for record attachments and creates groups for each attachment header
   * @param {Object} jsonData - The JSON data to process
   * @param {Array} validHeaders - The valid headers to process
   * returns {Object} headerGroups - {header: group name}
   * 
  */
  function processHeadersAndGroups(jsonData, validHeaders){
      const headerGroups = {};
      const validHeadersList = uploadProcessing.findValidHeaderKeys(jsonData, validHeaders);
      let groupNumber = 1;

      validHeadersList.forEach(header => {
        if (header.includes('-')) {
          let groupName = header.split('-').slice(1).join('-');
          headerGroups[header] = groupName === '' ? `group-${groupNumber++}` : groupName;
        } else {
          headerGroups[header] = `group-${groupNumber++}`;
        }
      });

      return headerGroups;
    }

  /**
   * Process the headers of a record to find attachments and their groups
   * @param {Object} record - The record to process
   * @param {Object} attachmentGroups - The groups to assign to each attachment eg header: group name
   * returns {Object} dataAttachments - {attachmentName: [group1, group2, ...] }
   */
  function processRecordAttachments(record, attachmentGroups) {
    // attachmentGroups = {header: group name}

    //object for each file name and the groups it is associated with
    let dataAttachments = {};

    // for each valid header, process the record
    Object.keys(attachmentGroups).forEach(header =>{
      
      let attachments = record[header];
      
      //if the header is not empty
      if (attachments) {
        // Split the attachments value by comma and process each one
        attachments.split(',').forEach(attachment => {

          let attachmentName = attachment.trim();
          if (attachmentName) {
            // Initialize the group array if it doesn't exist
            if (!dataAttachments[attachmentName]) {
              dataAttachments[attachmentName] = [];
            }

            // Add the group name if it's not already included
            if (!dataAttachments[attachmentName].includes(attachmentGroups[header])) {
              dataAttachments[attachmentName].push(attachmentGroups[header]);
            }
          }
        });
      }
    })
    return dataAttachments;
  }

  async function extractUserStringsFromRhinoFile(file) {
      //console.log(file);
      if (!file) return;
      try {
          const arrayBuffer = await file.originalFile.arrayBuffer();

          // Initialize rhino3dm with the path to the .wasm file
          var rhino3dmLoader = new Rhino3dmLoader();
          rhino3dmLoader.setLibraryPath(
            "https://cdn.jsdelivr.net/npm/rhino3dm@7.15.0/"
          );

          // Use a Promise to wait for the loader to parse the arrayBuffer
          const object = await new Promise((resolve, reject) => {
              rhino3dmLoader.parse(arrayBuffer, resolve, reject);
          });

          const userStringsArray = [];

          object.traverse((child) => {
              //console.log('child', child);  
              if (
                  child.userData &&
                  child.userData.attributes &&
                  child.userData.attributes.userStrings
              ) {
                  // Convert the array of key-value pairs into an object
                  const userStrings = {};
                  const userStringsPairs = child.userData.attributes.userStrings;

                  userStringsPairs.forEach(([key, value]) => {
                      userStrings[key] = value;
                  });

                  // if ellipseId prop does not exist
                  //Add the 'ellipseID' key using the child's 'name' property
                  if (!userStrings.hasOwnProperty('ellipseId')) {
                    //use child name or default to uuid
                      userStrings['ellipseId'] = child.name || child.uuid;
                  }

                  userStringsArray.push(userStrings);
              }
          });

          // Convert the array of objects into a JSON file
          const fileName = file.name;
          const jsonFile = await utils.stringToJsonFile(userStringsArray, fileName);

          //console.log('Extracted file:', jsonFile);
          // Add the JSON file to the userFiles array\
          ingestUserFiles([jsonFile]);

          return jsonFile;
      } catch (error) {
          console.error('Error reading the file:', error);
          throw error; // Re-throw the error to be caught in the calling code
      }
  }

  /**
   * Matches file extension to the appropriate mongo Schema Type Options.
   * @param {File} file 
   * @returns 
   */
  function getDataTypeOptions(file) {
    // Define the allowed types for each extension
    const extensionMapping = {
      '3dm': ['3D', 'Asset'],
      'svg':  ['2D', 'Asset'],
      'geojson': ['GIS', 'Asset'],
      'json': ['Data', 'Asset'],
      'csv':  ['Asset'],
      'jpeg': ['Asset', 'DataImg'],
      'jpg':  ['Asset', 'DataImg'],
      'png':  ['Asset', 'DataImg'],
      'bmp':  ['Asset', 'DataImg'],
      'gif':  ['Asset'],
    };

    // if File is not null
    if (!file) return;

    //console.log(file.name)

    // Get the file extension
    let extension = utils.getFileExtension(file.name).toLowerCase();

    // if the extension is not explicitly mapped, return only 'Asset'
    return extensionMapping[extension] || ['Asset'];
  }

  
/**
 * Unified uploader function to handle all user file uploads to s3. 
 * @param {string} url - API Endpoint to hit.
 * @param {*} files - Files to upload.
 * @param {*} path - s3 folder path to store the files.
 * @param {*} id - mongo record id.
 * @param {*} notebookId - notebook id.
 * @param {*} dataPayloadGenerator - method to format data payload (file vs othertypes).
 * @param {*} callback - callback function to update progress and mongo record.
 */
  async function uploadS3(
    url,
    files,
    path,
    id,
    notebookId,
    dataPayloadGenerator,
    callback
  ){

    //console.log('Upload Parameters:', {
    //   'uploadUrl': url,
    //   'files': files,
    //   'path': path,
    //   'id': id,
    //   'notebookId': notebookId,
    //   'fileName': files[0].name,
    //   'dataPayloadGenerator': dataPayloadGenerator,
    //   'callback': callback,
    //   'filetype': files[0].type,
    // });

    const authService = getInstance();
    let currentFile;
    const uploadProgresses = [];
    path = path.replace(/\/\/+/g, "/");

    for (let i = 0; i < files.length; i++) {
      currentFile = files[i];
      if (currentFile instanceof Promise) {
        currentFile = await currentFile;
      }

      await s3FileUpload(currentFile);
    }

    async function s3FileUpload(file) {

      //The s3 path to store the file.
      let s3PathKey = path + id + "?" + file.name;

      uploadProgresses.push({
        name: file.name,
        size: file.size,
        percent: 0,
        done: false,
      });

      let index = uploadProgresses.length - 1;
      callback(uploadProgresses);

      // Prepare the data payload using the provided generator function
      let dataPayload = dataPayloadGenerator(s3PathKey, file);

      // Make the API call to get the signed URL
      let response = await authService.$api.post(url, dataPayload);

      //check if signed url is in response.data.data[0].url OR response.data.data
      let signedUrl = response.data.data[0].url || response.data.data;
      
      let xhr = new XMLHttpRequest();
      xhr.open("PUT", signedUrl);
      
      // Extract content type from the signed URL
      const contentTypeMatch = signedUrl.match(/Content-Type=([^&]+)/);
      //const contentType = contentTypeMatch ? decodeURIComponent(contentTypeMatch[1]) : file.type 
      
      // Set the exact same content type that was used to generate the signed URL
      //xhr.setRequestHeader("Content-Type", contentType);
      
      xhr.upload.addEventListener("progress", uploadProgress, false);
      xhr.addEventListener("load", reqListener);

      function uploadProgress(evt) {
        const percentComplete = Math.round((evt.loaded * 100) / evt.total);
        uploadProgresses[index].percent = percentComplete;
        callback(uploadProgresses);
      }
      
      function reqListener() {
        xhr = this;
        // When finished uploading:
        if (xhr.status === 200) {
          uploadProgresses[index].done = true;
          callback(uploadProgresses, s3PathKey);
        } else {
          console.error('Upload failed with status', xhr.status, 'Response:', xhr.responseText);
        }
      }
      xhr.onerror = function () {
        console.error('Upload error for file:', file.name);
      };
      xhr.send(file.originalFile);
    }
  };

  async function uploadFileWithAxios(
    file,
    signedUrl,
  ) {
    //console.log('Uploading file with Axios:', file, signedUrl);
    try {
      // extract the content type from the signed URL
      // fall back to `file.type` if no match is found.
      const contentTypeMatch = signedUrl.match(/Content-Type=([^&]+)/);
      const contentType = contentTypeMatch
        ? decodeURIComponent(contentTypeMatch[1])
        : file.type; 

      // Upload the file 
      const response = await axios.put(
        signedUrl, 
        file.originalFile, {
        headers: {
          'Content-Type': contentType
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.lengthComputable) {
            const percentComplete = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            file.uploadPercentage = percentComplete *0.9;
            fileUpdateKey.value++;
          }
        }
      });

      // Check if the upload succeeded
      if (response.status === 200) {
        //file.uploadStatus = 's3 upload success, waiting for mongo record update';
        fileUpdateKey.value++;
      } else {
        //file.uploadStatus = 's3 upload failed';
        fileUpdateKey.value++;
        console.error(
          'Upload failed with status',
          response.status,
          'Response:',
          response.data
        );
      }

    } catch (error) {
      //file.uploadStatus = 's3 upload failed';
      fileUpdateKey.value++;
      console.error('Upload error for file:', file.name, 'Error:', error);
    }
  }

  /**
   * Process a batch entry of multiple files
   * @param {Object} batchEntry - The batch entry from userFiles
   */
  async function processBatchEntry(batchEntry) {
    if (!batchEntry.batchId || !fileBatches.value[batchEntry.batchId]) {
      return;
    }
    
    const batch = fileBatches.value[batchEntry.batchId];
    batchEntry.processingStatus = 'batch_processed';
    
    // Mark batch as ready for upload
    batch.processingStatus = 'ready';
    
    // Update processing stats
    processingDetails.value.stats.processed++;
    processingDetails.value.stats.pending--;
    processingDetails.value.stats.completed++;
    
    fileUpdateKey.value++;
  }

  //expose store
  return {

    fileTypeConfig,
    getUserFiles,
    getUploadStatus,
    getFileUpdateKey,
    getFileUploadDone,
    getUploadName,
    // Export the new progress tracking properties
    getTotalFilesToIngest,
    getIngestedFilesCount,
    getIsIngesting,
    getFileBatches,
    getProcessingDetails,
    setUploadName,
    updateFileUploadType,
    updateFileTags,
    triggerFileUpdateKey,
    getDataTypeOptions,
    ingestUserFiles,
    startUpload,
    removeUserFile,
    clearUserFiles,
    }
})